import { RevenueMetricCurrencyTicker } from "@/types/metrics"
import BigNumber from "bignumber.js"

BigNumber.config({ DECIMAL_PLACES: 20, ROUNDING_MODE: BigNumber.ROUND_DOWN })

/**
 * This file contains functions that are used to format amounts for storage in the database and transmission via the API,
 * and vice versa, to parse amounts from the database and API.
 */

/**
 * Maps a currency ticker to the number of decimal places it has.
 */
const currencyToDecimalPlaces: Record<RevenueMetricCurrencyTicker, number> = {
  USD: 2,
  ETH: 18,
  MATIC: 18,
  USDC: 6,
  GUSD: 2,
  WBTC: 8,
  WETH: 18,
  DAI: 18,
  BUSD: 18,
  FRAX: 18,
  ENS: 18,
  DYDX: 18,
  UNI: 18,
  USDT: 6,
  WMATIC: 18,
  WBNB: 18,
  USDCbc: 6,
  cbETH: 18,
}

/**
 * Maps a currency ticker to the minimum number of decimal places it should have.
 * This is used to ensure that the decimal places look human readable and as expected,
 * e.g. USD should show $1.20 instead of $1.2; while ETH should show 1.2 instead of 1.200000000000000000.
 */
const currencyToMinimumDecimalPlaces: Record<
  RevenueMetricCurrencyTicker,
  number | undefined
> = {
  USD: 2,
  ETH: undefined,
  MATIC: undefined,
  USDC: 2,
  GUSD: 2,
  WBTC: undefined,
  WETH: undefined,
  DAI: undefined,
  BUSD: 2,
  FRAX: undefined,
  ENS: undefined,
  DYDX: undefined,
  UNI: undefined,
  USDT: 2,
  WMATIC: undefined,
  WBNB: undefined,
  USDCbc: 2,
  cbETH: undefined,
}

/**
 * Gets the number of decimal places for a given currency ticker.
 * @param currency The currency ticker to get the number of decimal places for.
 * @returns The number of decimal places for the given currency ticker.
 */
export function getDecimalPlacesForCurrency(
  currency: RevenueMetricCurrencyTicker
): number {
  // TypeScript should ensure that all currencies are handled, but just in case, we'll default to 18 if one isn't found.
  return currencyToDecimalPlaces[currency] || 18
}

/**
 * Formats an amount number or string with decimal places, as a string with the no decimal places.
 * This is used for storage in the database and transmission via API.
 *
 * NOTE: This should only ever be used with numbers that are not already in the lowest denomination.
 * @param amount The amount to format.
 * @param currency The currency ticker to format the amount for.
 * @returns A string with the amount formatted as the lowest denomination (no decimal places).
 */
export function formatAmountAsLowestDenominationString(
  amount: number | string,
  currency: RevenueMetricCurrencyTicker
): string {
  const decimalPlaces = getDecimalPlacesForCurrency(currency)

  const bnAmount = new BigNumber(amount).multipliedBy(
    new BigNumber(10).pow(decimalPlaces)
  )

  return bnAmount.toFixed(0) // toFixed(0) ensures no decimal places are shown.
}

/**
 * Takes in a string amount in the lowest denomination (with no decimals), maps
 * it to the number of decimals for that currency, and returns a string with the
 * decimal places added back in.
 * @param amountWithNoDecimals The amount to format.
 * @param currency The currency ticker to format the amount for.
 * @param skipFormatting If true, the amount will not be formatted with commas.
 *                       This is useful if you plan to use the amount in a calculation.
 * @returns A string with the decimal places added back in.
 */
export function formatAmountAsHighestDenomString(
  amountWithNoDecimals: string,
  currency: RevenueMetricCurrencyTicker,
  skipFormatting?: boolean
): string {
  const decimalPlaces = getDecimalPlacesForCurrency(currency)

  // Convert the amount from its lowest denomination to a more readable format with decimal places.
  const bnAmount = new BigNumber(amountWithNoDecimals).dividedBy(
    new BigNumber(10).pow(decimalPlaces)
  )

  const number = bnAmount.toNumber()

  // Using Intl.NumberFormat for locale-aware formatting, including grouping separators.
  // Specify minimum and maximum fraction digits based on the currency's decimal places.
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: decimalPlaces,
    minimumFractionDigits: currencyToMinimumDecimalPlaces[currency],
    useGrouping: !skipFormatting,
  })

  // If there are no numbers after the decimal point, we want to truncate everything after the decimal point,
  // including the decimal point itself.
  if (number % 1 === 0) {
    formatter.format(number).split(".")[0]
  }

  return formatter.format(number)
}

/**
 * Takes in a string amount in the lowest denomination (with no decimals), maps
 * it to the number of decimals for that currency, and returns a string with the
 * rounded whole number (no decimal places) formatted.
 * @param amountWithNoDecimals The amount to format.
 * @param currency The currency ticker to format the amount for.
 * @returns A string with the decimal places added back in.
 */
export function formatAmountAsHighestDenomWholeNumberString(
  amountWithNoDecimals: string,
  currency: RevenueMetricCurrencyTicker
): string {
  const decimalPlaces = getDecimalPlacesForCurrency(currency)

  // Convert the amount from its lowest denomination to its highest denomination.
  const bnAmount = new BigNumber(amountWithNoDecimals).dividedBy(
    new BigNumber(10).pow(decimalPlaces)
  )

  // Round the amount to the nearest whole number and convert it back to a string.
  const wholeNumber = bnAmount
    .decimalPlaces(0, BigNumber.ROUND_HALF_UP)
    .toFixed()

  // Use Intl.NumberFormat for locale-aware formatting, including grouping separators.
  // Specify minimum and maximum fraction digits based on the currency's decimal places.
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })

  return formatter.format(Number(wholeNumber))
}

/**
 * Adds two amounts together and returns the result as a string.
 *
 * IMPORTANT! Make sure that both amounts are either in lowest, or highest denomination.
 * Do not try to add an amount in lowest denomination to an amount in highest denomination.
 * @param amount1 The first amount to add.
 * @param amount2 The second amount to add.
 * @returns A string with the result of adding the two amounts together.
 */
export function addTwoAmountStrings(amount1: string, amount2: string): string {
  if (isNaN(Number(amount1)) || isNaN(Number(amount2))) {
    console.error("Skipping addTwoAmountStrings function for NaN error", {
      amount1,
      amount2,
    })
  }

  const bnAmount1 = new BigNumber(amount1)
  const bnAmount2 = new BigNumber(amount2)

  const sum = bnAmount1.plus(bnAmount2)

  return sum.toFixed()
}

/**
 * Multiplies two strings (can be integers or floats) and returns the result as a string.
 * @param amount1 The first amount to multiply.
 * @param amount2 The second amount to multiply.
 * @returns The product as a string.
 */
export function multiplyTwoAmountStrings(
  amount1: string,
  amount2: string
): string {
  const bnAmount1 = new BigNumber(amount1)
  const bnAmount2 = new BigNumber(amount2)

  const product = bnAmount1.multipliedBy(bnAmount2)

  return product.toFixed() // Default behavior of toFixed is fine here.
}

/**
 * Compares two string amounts and returns a number indicating their relationship.
 * @param amount1 The first amount to compare.
 * @param amount2 The second amount to compare.
 * @returns The result of the comparison. -1 if amount1 < amount2, 0 if amount1 === amount2, 1 if amount1 > amount2.
 */
export function compareTwoStringAmounts(
  amount1: string,
  amount2: string
): number {
  const bnAmount1 = new BigNumber(amount1)
  const bnAmount2 = new BigNumber(amount2)

  return bnAmount1.comparedTo(bnAmount2)
}
